import { CompleteSvg, TodoSvg, WarningSvg } from "@/components/Icon/Icon";
import { StyledTooltip, TooltipBodyWithHeader } from "@/components/StyledTooltip/StyledTooltip";
import { OpenEventLogErrorLearnMoreDocs } from "@/constants/errorDocs";
import { GetEventLogErrorKeys } from "@/constants/errors";
import { useOrgCredits } from "@/hooks/credits";
import { CaseStatus, ErrorKey, ReadEventLog } from "@/store/clientApi";
import { Box, CircularProgress, Link, Theme, Typography, useTheme } from "@mui/material";
import { FC, ReactElement, ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";

interface CaseStatusIndicatorProps {
  caseStatus: CaseStatus | "unavailable";
  eventLogs: ReadEventLog[];
  iconOnly?: boolean;
  showExtendedErrors?: boolean;
}

interface BoxAroundIconProps {
  color?: string | ((theme: Theme) => string);
  children: ReactNode;
}

const caseStatusToIcon = (status: CaseStatus | "unavailable", eventLogs: ReadEventLog[]) => {
  const BoxAroundIcon: FC<BoxAroundIconProps> = ({ color, children }) => {
    return (
      <Box
        component="div"
        sx={{
          "& > svg": {
            color: color ?? ((theme) => theme.palette.success.main)
          },
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        {children}
      </Box>
    );
  };

  switch (status) {
    case "pending_upload":
    case "uploaded":
    case "files_converted":
    case "preprocessed":
      return (
        <CircularProgress
          size={"1.3em"}
          thickness={4.5}
          color="warning"
          sx={{
            padding: "0.1em",
            "&& > svg": { color: (theme: Theme) => theme.palette.warning.main }
          }}
        />
      );
    case "done":
      return GetEventLogErrorKeys(eventLogs, "warning", "status").errorKey !== undefined ? (
        <BoxAroundIcon color={(theme: Theme) => theme.palette.warning.main}>
          <WarningSvg width="1.3em" height="1.3em" />
        </BoxAroundIcon>
      ) : (
        <BoxAroundIcon>
          <TodoSvg width="1.3em" height="1.3em" />
        </BoxAroundIcon>
      );
    case "failed":
    case "rejected":
    case "unpaid_upload":
      return (
        <BoxAroundIcon color={(theme: Theme) => theme.palette.error.main}>
          <WarningSvg width="1.3em" height="1.3em" />
        </BoxAroundIcon>
      );
    case "downloaded":
      return (
        <BoxAroundIcon>
          <CompleteSvg width="1.3em" height="1.3em" />
        </BoxAroundIcon>
      );
  }
};

const EventLogTranslation = ({
  translationErrorKey,
  errorKey,
  defaultValue
}: {
  translationErrorKey?: string;
  errorKey?: ErrorKey;
  defaultValue: string | undefined;
}) => {
  const { t } = useTranslation("cases");
  const theme = useTheme();

  return (
    <Trans
      t={t}
      components={{
        a: (
          <Link
            color={theme.palette.primary.contrastText}
            component={"button"}
            onClick={(e) => {
              e.stopPropagation();
              if (errorKey) {
                OpenEventLogErrorLearnMoreDocs[errorKey]();
              }
            }}
          />
        )
      }}
      defaults={defaultValue}
      i18nKey={translationErrorKey}
    />
  );
};

const StatusText = ({
  status,
  eventLogs,
  showExtendedErrors
}: {
  status: CaseStatus | "unavailable";
  eventLogs: ReadEventLog[];
  showExtendedErrors: boolean;
}) => {
  const { t } = useTranslation("cases");
  const { isOutOfCredits } = useOrgCredits();

  switch (status) {
    case "pending_upload":
      return t("status.pendingUpload");
    case "uploaded":
      return t("status.uploaded");
    case "files_converted":
    case "preprocessed":
      return t("status.processing");
    case "failed":
      if (showExtendedErrors) {
        return <EventLogTranslation {...GetEventLogErrorKeys(eventLogs, "error", "status")} defaultValue={t("status.failed")} />;
      } else {
        return t("status.failed");
      }
    case "done": {
      const errorKeys = GetEventLogErrorKeys(eventLogs, "warning", "status");
      if (showExtendedErrors) {
        return <EventLogTranslation {...errorKeys} defaultValue={t("status.complete")} />;
      } else {
        return errorKeys.errorKey !== undefined ? t("title.completeWithIssues") : t("status.complete");
      }
    }
    case "downloaded":
      return t("status.downloaded");
    case "rejected":
      return t("status.rejected");
    case "unpaid_upload":
      if (isOutOfCredits) {
        return t("status.insufficientCredits");
      } else {
        return t("status.toProcess");
      }
    default:
    case "unavailable":
      return t("common:loading.unavailable");
  }
};

const StatusTooltip = ({
  status,
  eventLogs,
  children
}: {
  status: CaseStatus | "unavailable";
  eventLogs: ReadEventLog[];
  children: ReactElement;
}) => {
  const { t } = useTranslation("cases");
  if (status !== "failed" && status !== "done") {
    return <>{children}</>;
  }

  const errorType = status === "failed" ? "error" : "warning";
  const { translationErrorKey, errorKey } = GetEventLogErrorKeys(eventLogs, errorType, "message");
  if (translationErrorKey === undefined) {
    return <>{children}</>;
  }

  return (
    <StyledTooltip
      placement="left"
      title={
        <TooltipBodyWithHeader
          header={status === "done" ? t("title.completeWithIssues") : t("title.failed")}
          body={<EventLogTranslation translationErrorKey={translationErrorKey} errorKey={errorKey} defaultValue={undefined} />}
        />
      }
    >
      {children}
    </StyledTooltip>
  );
};

export const CaseStatusIndicator = ({ caseStatus, iconOnly, eventLogs, showExtendedErrors }: CaseStatusIndicatorProps) => {
  return (
    <StatusTooltip status={caseStatus} eventLogs={eventLogs}>
      <Box
        component="div"
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: "6px"
        }}
      >
        {caseStatusToIcon(caseStatus, eventLogs)}
        <Typography sx={{ cursor: "text", userSelect: "text", width: "max-content" }} noWrap onMouseDown={(e) => e.stopPropagation()}>
          {!iconOnly && <StatusText status={caseStatus} eventLogs={eventLogs} showExtendedErrors={showExtendedErrors ?? false} />}
        </Typography>
      </Box>
    </StatusTooltip>
  );
};
